import type { CalibrationPageData } from 'pages/reporting/reports/raut/rautReportData'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

interface NormalizeCalibrationFormDataOptions {
  geometricUnit: GeckoMeasurementUnit
  distanceUnit: GeckoMeasurementUnit
  hasPhasedArrayInspection: boolean
}

const HERTZ_PER_MHZ = 1_000_000

export const hertzToMegahertz = (
  value: number | undefined
): number | undefined => {
  if (value === undefined) {
    return undefined
  }

  return Number((value / HERTZ_PER_MHZ).toFixed(3))
}

export const formatAngleDegrees = (
  value: number | undefined
): string | undefined => {
  if (value === undefined) {
    return undefined
  }

  const rounded =
    Math.abs(value - Math.round(value)) < 0.001 ? Math.round(value) : value

  return GeckoMeasurementUnit.Degrees.formatNumber({
    num: rounded,
    locale: 'en-US',
    variant: undefined,
    maximumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
  })
}

export const normalizeTestingUnit = (
  platform: string | undefined,
  model: string | undefined
): string | undefined => {
  const combined = `${platform || ''} ${model || ''}`.toLowerCase()
  if (!combined.trim()) {
    return undefined
  }

  if (combined.includes('omniscan')) {
    return 'Omniscan'
  }

  return platform || model
}

export const normalizeModel = (
  value: string | undefined
): string | undefined => {
  if (!value) {
    return undefined
  }

  const trimmed = value.trim()
  const omniScanMatch = trimmed.match(/OmniScan\s+(X\d)\s*-\s*(\d+):(\d+)PR/i)
  if (omniScanMatch) {
    return `${omniScanMatch[1].toUpperCase()} ${omniScanMatch[2]}:${omniScanMatch[3]}`
  }

  const focusMatch = trimmed.match(/Focus\s+PX\s*-\s*(\d+):(\d+)/i)
  if (focusMatch) {
    return `Focus PX ${focusMatch[1]}:${focusMatch[2]}`
  }

  return trimmed
}

const toMeasurement = (
  value: unknown,
  unit: GeckoMeasurementUnit
): GeckoMeasurement | undefined => {
  if (value instanceof GeckoMeasurement) {
    return value.convertTo(unit)
  }

  if (
    value &&
    typeof value === 'object' &&
    'num' in value &&
    typeof value.num === 'number'
  ) {
    return GeckoMeasurement.CreateFromValue(value.num, unit)
  }

  return undefined
}

const processNestedMeasurements = <T>(
  obj: T | undefined,
  measurementPaths: Array<{ path: string[]; unit: GeckoMeasurementUnit }>
): T => {
  const result = { ...(obj || {}) } as Record<string, unknown>

  measurementPaths.forEach(({ path, unit }) => {
    let current = result

    for (let i = 0; i < path.length - 1; i++) {
      const nextValue = current[path[i]]
      current[path[i]] =
        nextValue && typeof nextValue === 'object'
          ? { ...(nextValue as Record<string, unknown>) }
          : {}
      current = current[path[i]] as Record<string, unknown>
    }

    const lastProp = path[path.length - 1]
    const measurement = toMeasurement(current[lastProp], unit)
    if (measurement) {
      current[lastProp] = measurement
    }
  })

  return result as T
}

export const normalizeCalibrationFormData = (
  values: CalibrationPageData,
  {
    geometricUnit,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    distanceUnit: _distanceUnit,
    hasPhasedArrayInspection,
  }: NormalizeCalibrationFormDataOptions
): CalibrationPageData => {
  const toleranceValue =
    geometricUnit === GeckoMeasurementUnit.Inches ? 0.005 : 0.127

  const globalMeasurementPaths = [
    { path: ['xMotor', 'scanRes'], unit: geometricUnit },
    { path: ['xMotor', 'speed'], unit: geometricUnit },
    { path: ['yMotor', 'scanRes'], unit: geometricUnit },
    { path: ['yMotor', 'speed'], unit: geometricUnit },
  ]

  const normalizedValues = processNestedMeasurements(
    values,
    globalMeasurementPaths
  )

  if (normalizedValues.techniques) {
    normalizedValues.techniques = normalizedValues.techniques.map(
      (technique) => {
        const techniqueMeasurementPaths = [
          { path: ['pitch'], unit: geometricUnit },
          { path: ['velocity'], unit: geometricUnit },
          {
            path: ['calibration', 'thinStepDistance'],
            unit: geometricUnit,
          },
          {
            path: ['calibration', 'thickStepDistance'],
            unit: geometricUnit,
          },
          {
            path: ['calibration', 'verificationStepDistance'],
            unit: geometricUnit,
          },
        ]

        const processedTechnique = processNestedMeasurements(
          technique,
          techniqueMeasurementPaths
        )

        if (processedTechnique.calibration) {
          processedTechnique.calibration.thinStepTolerance =
            GeckoMeasurement.CreateFromValue(toleranceValue, geometricUnit)
          processedTechnique.calibration.thickStepTolerance =
            GeckoMeasurement.CreateFromValue(toleranceValue, geometricUnit)
          processedTechnique.calibration.verificationStepTolerance =
            GeckoMeasurement.CreateFromValue(toleranceValue, geometricUnit)
        }

        return processedTechnique
      }
    )
  }

  if (hasPhasedArrayInspection && normalizedValues.angleBeamCalibration) {
    normalizedValues.angleBeamCalibration =
      normalizedValues.angleBeamCalibration.map((entry) => {
        if (entry.distance && typeof entry.distance === 'object') {
          return {
            ...entry,
            distance: processNestedMeasurements({ distance: entry.distance }, [
              { path: ['distance'], unit: geometricUnit },
            ]).distance as GeckoMeasurement,
          }
        }
        return entry
      })
  }

  return normalizedValues
}
